import { Component, h, Prop, State, Watch } from "@stencil/core"
import { PaymentMethodEnum } from "../../enums/PaymentMethodEnum"
import { validatePaymentMethodEnum } from "../../helpers/Validators"
import { HorizontalMarkSizeEnum, HorizontalMarkSizeEnumsObj } from "../../enums/HorizontalMarkSizeEnum"
import { InfoMessageModel, MessageMaxLength } from "../infoMessage/InfoMessageModel"
import { LoginScreenStateModel } from "./LoginScreenStateModel"
import { srcScreenError } from "../../helpers/CommonRenders"
import LanguageHandler from "@payair/e-com-common-sdk/core/LanguageHandler"
import { getSupportedLocaleData, getLanguageHandler, getLocale } from "../../languageHandler/LanguageHandler"

/**
 * Entire screen to handle login into the Click to Pay System.
 */
@Component({
  tag: "login-screen",
  styleUrl: "LoginScreen.scss",
})
export class LoginScreen {
  /**
   * Merchant site preferred locale. Format based on ISO format for language (ISO 639-1) and country code
   * (ISO 3166-1 alpha-2). The language and country should be separated using an underscore ( _ ).
   * <br /><br />
   * **Supported locale**: en_GB, sv_SE, pl_PL
   * <br /><br />
   * @default en_GB
   */
  @Prop() locale?: string

  /**
   * Indicator which Click to Pay System is supported.
   */
  @Prop() paymentMethod: PaymentMethodEnum

  /**
   * Data source for alert which can be displayed on the AddCardScreen.
   */
  @Prop() infoMessage?: InfoMessageModel | undefined

  /**
   * Indicator if the screen is loading.
   *
   * @default false
   */
  @Prop() isLoading?: boolean

  /**
   * Callback called after close button click.
   */
  @Prop() closeButtonOnClick?: () => void

  /**
   * Callback called after continue button click
   *
   * @param loginScreenState Object with the email value passed by the customer.
   */
  @Prop() continueButtonOnClick?: (loginScreenState: LoginScreenStateModel) => void

  /**
   * Callback called after new account click.
   */
  @Prop() newAccountOnClick?: () => void

  @State() email: string = ""
  @State() isLoadingState: boolean = this.isLoading === true ? true : false

  private languageHandler: LanguageHandler
  private horizontalMarkSize: HorizontalMarkSizeEnum
  private errorMessage: string[]
  private supportedLocale: string

  componentWillLoad() {
    this.errorMessage = []

    if (!this.paymentMethod) {
      this.errorMessage.push("'paymentMethod' prop is not defined")
    } else if (!validatePaymentMethodEnum(this.paymentMethod)) {
      this.errorMessage.push("'paymentMethod' prop is not valid")
    }

    if (this.errorMessage.length > 0) {
      return
    }

    this.horizontalMarkSize = HorizontalMarkSizeEnumsObj.S16

    const localeData = getSupportedLocaleData(this.locale)
    this.languageHandler = getLanguageHandler(localeData.lang)
    this.supportedLocale = getLocale(localeData)

    if (this.infoMessage && this.infoMessage.infoMessage.length > MessageMaxLength.LOGIN_SCREEN) {
      this.infoMessage.infoMessage = this.infoMessage.infoMessage.substring(0, MessageMaxLength.LOGIN_SCREEN)
    }
  }

  private submit = () => {
    if (typeof this.continueButtonOnClick === "function" && this.isLoading !== true) {
      this.continueButtonOnClick({ email: this.email })
    }
  }

  renderLoginScreen = () => {
    const controlledEmailInputProps = {
      value: this.email,
      showValidationError: false,
      onValueChange: (event: CustomEvent<string>) => {
        this.email = event.detail
      },
    }

    return (
      <email-input
        locale={this.supportedLocale}
        {...controlledEmailInputProps}
        onKeyDown={event => {
          if (event.key === "Enter") {
            this.submit()
          }
        }}></email-input>
    )
  }

  @Watch("isLoading")
  updateIsLoadingState() {
    this.isLoadingState = this.isLoading === true ? true : false
  }

  render() {
    if (this.errorMessage.length > 0) {
      return srcScreenError("Login Screen", this.errorMessage)
    }

    return (
      <div class="loginScreen">
        <div class="closeButtonContainer">
          {typeof this.closeButtonOnClick === "function" && (
            <close-button closeButtonOnClick={this.closeButtonOnClick}></close-button>
          )}
        </div>

        <div class="loginScreenContent">
          <div class="greetingText">{this.languageHandler.getString("welcomeBackToClickToPay")}</div>

          <div class="descriptionText">{this.languageHandler.getString("fastSecureOnlineCheckout")}</div>

          <div class="horizontalMarkContainer">
            <horizontal-mark
              locale={this.supportedLocale}
              paymentMethod={this.paymentMethod}
              horizontalMarkSize={this.horizontalMarkSize}></horizontal-mark>
          </div>

          {this.infoMessage && Boolean(this.infoMessage.infoMessage) && (
            <info-message infoMessage={this.infoMessage} class={"infoMessage"} />
          )}

          {this.renderLoginScreen()}

          <div class="continueButtonContainer">
            <button class="continueButton" onClick={this.submit} disabled={this.isLoading}>
              {this.isLoadingState === false ? (
                <div>{this.languageHandler.getString("continue")}</div>
              ) : (
                <loading-spinner loadingText={this.languageHandler.getString("processing")} />
              )}
            </button>
            <div class="newAccountLink">
              <switch-user
                locale={this.supportedLocale}
                switchUserText={this.languageHandler.getString("createAccount")}
                changeUserButtonOnClick={this.newAccountOnClick}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }
}
